import React from 'react';

/**
 * The Noodle Studio mark: a strand between two ports, the connector the canvas draws between
 * two cards. One cubic curve, two open rings, nothing filled (docs/DESIGN_SYSTEM.md, "Brand").
 *
 * `tone` says where it sits: on the cream ground (lavender-deep strand, ink ports), on a dark
 * surface (lavender strand, cream ports), on the brand lavender (white strand, ink ports), or in
 * one colour (ink, for favicons and print). The colours are the kit's variables, so the mark
 * follows the theme.
 */
export type BrandMarkTone = 'light' | 'onDark' | 'onBrand' | 'mono';

const TONES: Record<BrandMarkTone, { strand: string; port: string; portFill: string }> = {
  light: { strand: 'var(--lavender-deep)', port: 'var(--ink)', portFill: 'var(--bg)' },
  onDark: { strand: 'var(--lavender)', port: 'var(--bg)', portFill: 'var(--ink)' },
  onBrand: { strand: '#FFFFFF', port: 'var(--ink)', portFill: 'var(--lavender)' },
  mono: { strand: 'currentColor', port: 'currentColor', portFill: 'transparent' },
};

interface BrandMarkProps {
  size?: number;
  tone?: BrandMarkTone;
  className?: string;
  /** Said to screen readers; left out, the mark is decoration */
  label?: string;
}

export const BrandMark: React.FC<BrandMarkProps> = ({ size = 24, tone = 'light', className = '', label }) => {
  const c = TONES[tone];
  // Below 24px the strokes thicken, so the strand and the rings survive a favicon
  const small = size < 24;
  const strand = small ? 9 : 7;
  const ring = small ? 6 : 4;
  const r = small ? 7 : 5.5;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      className={className}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      data-brand-mark
    >
      <path d="M8 40 C 14 22, 26 22, 32 32 C 38 42, 50 42, 56 24" stroke={c.strand} strokeWidth={strand} strokeLinecap="round" />
      <circle cx="8" cy="40" r={r} fill={c.portFill} stroke={c.port} strokeWidth={ring} />
      <circle cx="56" cy="24" r={r} fill={c.portFill} stroke={c.port} strokeWidth={ring} />
    </svg>
  );
};

/**
 * The word beta, riding on the name everywhere until it is no longer true (the A14 brand boards):
 * a small chip in the brand tint, or lemon where it stands alone in a line of facts.
 */
export const BetaTag: React.FC<{ tone?: 'brand' | 'lemon'; className?: string }> = ({ tone = 'brand', className = '' }) => (
  <span
    className={`inline-flex items-center h-[18px] px-2 rounded-full text-2xs font-bold tracking-[0.08em] uppercase leading-none ${tone === 'lemon' ? 'bg-lemon-soft text-lemon-deep' : 'bg-lavender-soft text-lavender-deep'} ${className}`}
    title="Noodle Studio is in beta: things change weekly, and some corners are rough"
    data-beta-tag
  >
    Beta
  </span>
);

/** The mark beside the name, as in the top bar and the page headers; the beta tag rides along unless told not to */
export const BrandLockup: React.FC<{ size?: number; tone?: BrandMarkTone; className?: string; beta?: boolean }> = ({ size = 28, tone = 'light', className = '', beta = true }) => (
  <span className={`inline-flex items-center gap-2.5 ${className}`}>
    <BrandMark size={size} tone={tone} />
    <span className="font-bold tracking-[-0.02em]" style={{ fontSize: Math.round(size * 0.6) }}>Noodle Studio</span>
    {beta && <BetaTag />}
  </span>
);

/**
 * The strand as a loader: the dot travels the curve while something is on its way
 * (.ns-strand-loader in index.css). Decorative; the words beside it say what is happening.
 */
export const StrandLoader: React.FC<{ width?: number; className?: string }> = ({ width = 56, className = '' }) => (
  <svg width={width} height={Math.round(width * 0.5)} viewBox="0 0 64 32" fill="none" className={`ns-strand-loader ${className}`} aria-hidden="true">
    <path className="ns-strand-loader__strand" d="M6 24 C 12 6, 24 6, 32 16 C 40 26, 52 26, 58 8" stroke="var(--lavender-deep)" strokeWidth="6" strokeLinecap="round" />
    <circle className="ns-strand-loader__port" cx="6" cy="24" r="5" fill="var(--bg)" stroke="var(--ink)" strokeWidth="3.5" />
    <circle className="ns-strand-loader__port ns-strand-loader__port--end" cx="58" cy="8" r="5" fill="var(--bg)" stroke="var(--ink)" strokeWidth="3.5" />
  </svg>
);

export default BrandMark;
